import { Link } from 'react-router-dom';
import { usePdfExport } from '../hooks/usePdfExport';
import { useCharacterBenefits } from '../hooks/useCharacterBenefits';
import { useCharacterCreationStore } from '../stores/characterCreationStore';
import { usePlaySessionStore } from '../stores/playSessionStore';
import { ABILITY_NAMES } from '@character-creator/types';
import { formatModifier, formatAbility, formatSkill } from '../utils/formatting';
import { downloadCharacterJson } from '../utils/characterExport';
import { PdfExportButton } from '../components/ui/PdfExportButton';

export function CharacterSheetPage() {
  const { error } = usePdfExport();
  const benefits = useCharacterBenefits();
  const { draft } = useCharacterCreationStore();
  const { getExportableRuntimeState } = usePlaySessionStore();

  const handleExportJson = () => {
    const runtimeState = getExportableRuntimeState();
    downloadCharacterJson(draft, runtimeState ?? undefined);
  };

  // Format class display
  const classDisplay =
    benefits.classLevels.length > 0
      ? benefits.classLevels.map((cl) => `${cl.className} ${cl.level}`).join(' / ')
      : 'Commoner 1';

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-text-primary mb-2">
            {draft.name || 'Unnamed Character'}
          </h1>
          <p className="text-text-secondary">{classDisplay}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportJson} className="btn-secondary">
            Export JSON
          </button>
          <PdfExportButton
            variant="primary"
            characterName={draft.name || 'Unnamed Character'}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Character Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Ability Scores */}
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Ability Scores</h3>
          <div className="grid grid-cols-2 gap-2">
            {ABILITY_NAMES.map((ability) => (
              <div key={ability} className="p-2 bg-background-secondary rounded text-center">
                <div className="text-xs text-text-muted">{ability.slice(0, 3).toUpperCase()}</div>
                <div className="font-bold text-text-primary">{benefits.finalScores[ability]}</div>
                <div className="text-sm text-text-secondary">
                  {formatModifier(benefits.modifiers[ability])}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Combat Stats */}
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Combat Stats</h3>
          <div className="space-y-2">
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Armor Class</span>
              <span className="font-bold text-text-primary">
                {10 + benefits.modifiers.dexterity}
              </span>
            </div>
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Hit Points</span>
              <span className="font-bold text-text-primary">{benefits.hitPoints}</span>
            </div>
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Speed</span>
              <span className="font-bold text-text-primary">{benefits.speed} ft</span>
            </div>
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Prof. Bonus</span>
              <span className="font-bold text-text-primary">
                {formatModifier(benefits.proficiencyBonus)}
              </span>
            </div>
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Initiative</span>
              <span className="font-bold text-text-primary">
                {formatModifier(benefits.modifiers.dexterity)}
              </span>
            </div>
            <div className="flex justify-between p-2 bg-background-secondary rounded">
              <span className="text-text-muted">Hit Dice</span>
              <span className="font-bold text-text-primary">
                {benefits.hitDice.map((hd) => `${hd.count}${hd.die}`).join(' + ') || '1d4'}
              </span>
            </div>
          </div>
        </div>

        {/* Saving Throws */}
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Saving Throws</h3>
          <div className="space-y-1">
            {ABILITY_NAMES.map((ability) => {
              const isProficient = benefits.savingThrows.includes(ability);
              const modifier = isProficient
                ? benefits.modifiers[ability] + benefits.proficiencyBonus
                : benefits.modifiers[ability];
              return (
                <div
                  key={ability}
                  className="flex justify-between p-2 bg-background-secondary rounded"
                >
                  <span className="text-text-muted flex items-center gap-2">
                    {isProficient && <span className="w-2 h-2 bg-accent-gold rounded-full" />}
                    {formatAbility(ability)}
                  </span>
                  <span className="font-bold text-text-primary">{formatModifier(modifier)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Skills */}
      <div className="card p-4">
        <h3 className="font-display font-semibold text-text-primary mb-3">Skills</h3>
        <div className="grid md:grid-cols-3 gap-2">
          {(
            [
              'acrobatics',
              'animal-handling',
              'arcana',
              'athletics',
              'deception',
              'history',
              'insight',
              'intimidation',
              'investigation',
              'medicine',
              'nature',
              'perception',
              'performance',
              'persuasion',
              'religion',
              'sleight-of-hand',
              'stealth',
              'survival',
            ] as const
          ).map((skill) => {
            const isProficient = benefits.skills.includes(skill);
            const hasExpertise = benefits.expertiseSkills.includes(skill);
            // Calculate based on SKILL_ABILITY_MAP
            const abilityMap: Record<string, keyof typeof benefits.modifiers> = {
              acrobatics: 'dexterity',
              'animal-handling': 'wisdom',
              arcana: 'intelligence',
              athletics: 'strength',
              deception: 'charisma',
              history: 'intelligence',
              insight: 'wisdom',
              intimidation: 'charisma',
              investigation: 'intelligence',
              medicine: 'wisdom',
              nature: 'intelligence',
              perception: 'wisdom',
              performance: 'charisma',
              persuasion: 'charisma',
              religion: 'intelligence',
              'sleight-of-hand': 'dexterity',
              stealth: 'dexterity',
              survival: 'wisdom',
            };
            const abilityMod = benefits.modifiers[abilityMap[skill]];
            const modifier = hasExpertise
              ? abilityMod + benefits.proficiencyBonus * 2
              : isProficient
                ? abilityMod + benefits.proficiencyBonus
                : abilityMod;
            return (
              <div
                key={skill}
                className="flex justify-between p-2 bg-background-secondary rounded text-sm"
              >
                <span className="text-text-muted flex items-center gap-2">
                  {hasExpertise && <span className="w-2 h-2 bg-accent-gold rounded-full ring-2 ring-accent-gold ring-offset-1 ring-offset-background-secondary" />}
                  {isProficient && !hasExpertise && <span className="w-2 h-2 bg-accent-gold rounded-full" />}
                  {formatSkill(skill)}
                  {hasExpertise && <span className="text-xs text-accent-gold">(E)</span>}
                </span>
                <span className="font-bold text-text-primary">{formatModifier(modifier)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Racial Traits */}
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Racial Traits</h3>
          {benefits.racialTraits.length > 0 ? (
            <div className="space-y-2">
              {benefits.racialTraits.map((trait) => (
                <div key={trait.id} className="p-2 bg-background-secondary rounded">
                  <div className="text-sm font-medium text-text-primary">{trait.name}</div>
                  <div className="text-xs text-text-muted line-clamp-2">{trait.description}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-text-muted text-sm">No racial traits selected</p>
          )}
        </div>

        {/* Class Features */}
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Class Features</h3>
          {benefits.classFeatures.length > 0 ? (
            <div className="space-y-2">
              {benefits.classFeatures.map((feature) => (
                <div key={feature.id} className="p-2 bg-background-secondary rounded">
                  <div className="text-sm font-medium text-text-primary">
                    {feature.name}{' '}
                    <span className="text-text-muted font-normal">(Lvl {feature.level})</span>
                  </div>
                  <div className="text-xs text-text-muted line-clamp-2">{feature.description}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-text-muted text-sm">No class features</p>
          )}
        </div>
      </div>

      {/* Proficiencies & Languages */}
      <div className="card p-4">
        <h3 className="font-display font-semibold text-text-primary mb-3">
          Proficiencies & Languages
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-text-secondary mb-2">Languages</h4>
            <p className="text-text-primary">{benefits.languages.join(', ') || 'Common'}</p>
          </div>
          {benefits.armor.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">Armor</h4>
              <p className="text-text-primary">{benefits.armor.join(', ')}</p>
            </div>
          )}
          {benefits.weapons.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">Weapons</h4>
              <p className="text-text-primary">{benefits.weapons.join(', ')}</p>
            </div>
          )}
          {benefits.tools.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-2">Tools</h4>
              <p className="text-text-primary">{benefits.tools.join(', ')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Spellcasting */}
      {benefits.spellcasting && (
        <div className="card p-4">
          <h3 className="font-display font-semibold text-text-primary mb-3">Spellcasting</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-2 bg-background-secondary rounded">
              <div className="text-xs text-text-muted">Ability</div>
              <div className="font-bold text-text-primary">
                {formatAbility(benefits.spellcasting.ability).slice(0, 3).toUpperCase()}
              </div>
            </div>
            <div className="text-center p-2 bg-background-secondary rounded">
              <div className="text-xs text-text-muted">Spell Save DC</div>
              <div className="font-bold text-text-primary">{benefits.spellcasting.saveDC}</div>
            </div>
            <div className="text-center p-2 bg-background-secondary rounded">
              <div className="text-xs text-text-muted">Spell Attack</div>
              <div className="font-bold text-text-primary">
                {formatModifier(benefits.spellcasting.attackBonus)}
              </div>
            </div>
            <div className="text-center p-2 bg-background-secondary rounded">
              <div className="text-xs text-text-muted">Cantrips Known</div>
              <div className="font-bold text-text-primary">{benefits.spellcasting.cantripsKnown}</div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Link to="/create" className="btn-secondary">
          Edit Character
        </Link>
        <Link to="/" className="btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
}
